import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/layout/Header';
import NavigationWrapper from './components/layout/NavigationWrapper';
import MainContent from './components/layout/MainContent';
import Footer from './components/layout/Footer';
import Hero from './components/home/Hero';
import Welcome from './components/PLG/Welcome';
import AuthModal from './components/auth/AuthModal';
import Profile from './components/user/Profile';
import AddReview from './components/reviews/AddReview';
import ReviewList from './components/reviews/ReviewList';
import SocialFeed from './components/social/SocialFeed';
import SimpleRec from './components/recs/SimpleRec';
import { supabase } from './lib/supabase';
import OnboardingNew from './components/PLG/OnboardingNew';
import UserTeardown from './components/dweeb/UserTeardown';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import useMediaQuery from './hooks/useMediaQuery';
import posthog from 'posthog-js';

posthog.init('phc_n6QTlWa5BZVtfIvQbdFuDOEO1pbXAEFgI1u14aP5GtH', {
  api_host: 'https://eu.i.posthog.com',
  person_profiles: 'identified_only'
});

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

const Recommendations = ({ user }) => <SimpleRec user={user} />;

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const syncUser = async (session) => {
    try {
      console.log('🔄 Attempting to sync user:', {
        id: session.user.id,
        email: session.user.email,
        metadata: session.user.user_metadata
      });
      const payload = {
        supabase_id: session.user.id,
        email: session.user.email
      };
      console.log('🔄 Making API request to:', `${API_BASE}/v1/users/sync`);
      const res = await fetch(`${API_BASE}/v1/users/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        console.warn('⚠️ User sync failed:', res.status, msg);
        console.warn('Request details:', {
          url: `${API_BASE}/v1/users/sync`,
          payload,
          status: res.status
        });
      } else {
        const data = await res.json();
        console.log('✅ User synced successfully:', data);
      }
      } catch (err) {
      console.error('❌ Sync error:', err);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        syncUser(session);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state changed:', event, session?.user?.email);
      setUser(session?.user ?? null);
      if (session?.user && ['SIGNED_IN', 'TOKEN_REFRESHED'].includes(event)) {
        setIsAuthModalOpen(false);
        await syncUser(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
  if (user) {
      setProfileLoading(true);
      fetch(`${API_BASE}/v1/users/me`, {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-User-ID': user.id,
        },
      })
      .then((res) => res.json())
      .then((data) => {
        setProfile(data);
          setProfileLoading(false);
        })
        .catch(() => {
          setProfileLoading(false);
      });
    } else {
      setProfile(null);
      setProfileLoading(false);
    }
  }, [user?.id]);

  const handleLogin = () => {
    setIsAuthModalOpen(true);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  if (user && profileLoading) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white">Loading...</div>;
  }
    return (
    <Router>
      <NavigationProvider>
        <div className="min-h-screen bg-black text-white flex flex-col">
          {user && profile && profile.onboarding_completed === false ? (
            <OnboardingNew
              user={user}
              onProfileUpdate={setProfile}
              onDone={() => {
                setProfile((prev) => ({ ...prev, onboarding_completed: true }));
              }}
            />
          ) : (
            <>
              <Header user={user} onLogin={handleLogin} onLogout={handleLogout} />
              {user && <NavigationWrapper />}
              <MainContent>
                <Routes>
                  {user ? (
                    <>
                      <Route path="/profile" element={<Profile user={user} />} />
                      <Route path="/" element={<SocialFeed user={user} />} />
                      <Route path="/reviews" element={<AddReview variant="standalone" />} />
                      <Route path="/watchlist" element={<ReviewList user={user} />} />
                      <Route path="/recs" element={<Recommendations user={user} />} />
                      <Route path="/dweeb" element={<UserTeardown user={user} />} />
                    </>
                  ) : (
                    <Route path="/" element={<Hero />} />
                  )}
                </Routes>
              </MainContent>
              <Footer user={user} onLogin={handleLogin} onLogout={handleLogout} />
              <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
            </>
          )}
        </div>
      </NavigationProvider>
    </Router>
  );
}

